'use client';
import { motion } from 'motion/react';

interface LogoProps {
  className?: string;
  showText?: boolean;
  size?: 'sm' | 'md' | 'lg';
  animated?: boolean;
}

export default function Logo({
  className = '',
  showText = true,
  size = 'md',
  animated = true,
}: LogoProps) {
  const sizes = {
    sm: { icon: 32, text: 'text-lg' },
    md: { icon: 40, text: 'text-xl' },
    lg: { icon: 56, text: 'text-2xl' },
  };

  const { icon: iconSize, text: textSize } = sizes[size];

  const MotionWrapper = animated ? motion.div : 'div';
  const wrapperProps = animated
    ? {
      whileHover: { scale: 1.03 },
      whileTap: { scale: 0.98 },
    }
    : {};

  return (
    <MotionWrapper
      className={`flex items-center gap-2.5 cursor-pointer ${className}`}
      {...wrapperProps}
    >
      {/* Logo Icon - Abstract Impact Symbol */}
      <div
        className="relative"
        style={{ width: iconSize, height: iconSize }}
      >
        <svg
          viewBox="0 0 48 48"
          fill="none"
          className="w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Outer hexagon - industrial feel */}
          <motion.path
            d="M24 4L42 14V34L24 44L6 34V14L24 4Z"
            fill="hsl(var(--slate-deep))"
            initial={animated ? { scale: 0.8, opacity: 0 } : {}}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          />

          {/* Impact rings - emanating from center */}
          <motion.circle
            cx="24"
            cy="24"
            r="14"
            stroke="hsl(var(--amber))"
            strokeWidth="1.5"
            fill="none"
            opacity="0.3"
            initial={animated ? { scale: 0, opacity: 0 } : { opacity: 0.3 }}
            animate={animated ? { scale: [0.5, 1.2], opacity: [0.5, 0] } : {}}
            transition={
              animated
                ? { duration: 2, repeat: Infinity, ease: 'easeOut' }
                : {}
            }
          />
          <motion.circle
            cx="24"
            cy="24"
            r="10"
            stroke="hsl(var(--amber))"
            strokeWidth="1.5"
            fill="none"
            opacity="0.5"
            initial={animated ? { scale: 0, opacity: 0 } : { opacity: 0.5 }}
            animate={animated ? { scale: [0.5, 1.4], opacity: [0.6, 0] } : {}}
            transition={
              animated
                ? { duration: 2, repeat: Infinity, ease: 'easeOut', delay: 0.5 }
                : {}
            }
          />

          {/* Center impact point */}
          <motion.circle
            cx="24"
            cy="24"
            r="6"
            fill="hsl(var(--amber))"
            initial={animated ? { scale: 0 } : {}}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          />

          {/* Strike lines - representing data/metrics */}
          <motion.path
            d="M24 10V18"
            stroke="hsl(var(--amber))"
            strokeWidth="2"
            strokeLinecap="round"
            initial={animated ? { pathLength: 0 } : {}}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          />
          <motion.path
            d="M24 30V38"
            stroke="hsl(var(--amber))"
            strokeWidth="2"
            strokeLinecap="round"
            initial={animated ? { pathLength: 0 } : {}}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.4, delay: 0.35 }}
          />
          <motion.path
            d="M10 24H18"
            stroke="hsl(var(--amber))"
            strokeWidth="2"
            strokeLinecap="round"
            initial={animated ? { pathLength: 0 } : {}}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.4, delay: 0.4 }}
          />
          <motion.path
            d="M30 24H38"
            stroke="hsl(var(--amber))"
            strokeWidth="2"
            strokeLinecap="round"
            initial={animated ? { pathLength: 0 } : {}}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.4, delay: 0.45 }}
          />

          {/* Diagonal accent lines */}
          <motion.path
            d="M14 14L18 18"
            stroke="hsl(var(--amber))"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.6"
            initial={animated ? { pathLength: 0 } : {}}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          />
          <motion.path
            d="M34 34L30 30"
            stroke="hsl(var(--amber))"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.6"
            initial={animated ? { pathLength: 0 } : {}}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.3, delay: 0.55 }}
          />
          <motion.path
            d="M34 14L30 18"
            stroke="hsl(var(--amber))"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.6"
            initial={animated ? { pathLength: 0 } : {}}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.3, delay: 0.6 }}
          />
          <motion.path
            d="M14 34L18 30"
            stroke="hsl(var(--amber))"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.6"
            initial={animated ? { pathLength: 0 } : {}}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.3, delay: 0.65 }}
          />
        </svg>
      </div>

      {/* Text */}
      {showText && (
        <motion.span
          className={`font-serif font-semibold tracking-tight ${textSize}`}
          initial={animated ? { opacity: 0, x: -10 } : {}}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <span className="text-[hsl(var(--slate-deep))]">Impact</span>
          <span className="text-[hsl(var(--amber))]">OT</span>
        </motion.span>
      )}
    </MotionWrapper>
  );
}
